import React, { ReactNode, memo, useCallback, useMemo } from 'react';
import { useRouter } from 'next/compat/router';
import styled, { css } from 'styled-components';
import Link from 'components/core/link';

import { Product } from 'types/graphql-overrides';
import { ProductCardTextAlignmentOption } from 'types/graphql';
import { useCommerceComponents } from 'src/theme/provider/commerce-components/context';
import { mapToGridProps } from 'src/theme/provider/commerce-components/bridge/bridge-map-product-card';

import { EmbeddedExternalLink } from 'src/components/core';
import { useProductCardData } from './use-product-card-data';
import { CardProvider, useCardContext } from './card-context';
import { CardImage } from './card-image';
import { CardSponsoredTag } from './card-sponsored-tag';
import { CardName } from './card-name';
import { CardBrand } from './card-brand';
import { CardStrainDetails } from './card-strain-details';
import { CardTags } from './card-tags';
import { CardPrice } from './card-price';
import { CardActionButton } from './card-action-button';

type CardProps = {
  product: Product;
  productIndex: number;
  hideSponsoredTag?: boolean;
  onClick: (product: Product, index: number) => void;
  onQuickAddClick: (product: Product, index: number) => void;
  onView?: (product: Product, index: number) => void;
  'data-testid'?: string;
  isSmallCard?: boolean;
  isCarousel?: boolean;
};

const DefaultCardSentinel: React.FC<any> = () => null;

/**
 * Container for the grid product card.
 * Owns data fetching and action binding; delegates rendering to composable sub-components via context, or to a custom remote component.
 */
const Card = ({
  product,
  productIndex,
  hideSponsoredTag = false,
  onClick,
  onQuickAddClick,
  onView,
  'data-testid': testId,
  isSmallCard = false,
  isCarousel = false,
}: CardProps) => {
  const { components } = useCommerceComponents();
  const ResolvedCard = useMemo(
    () => components.ProductCard({ fallback: DefaultCardSentinel }),
    [components]
  );
  const hasCustomCard = ResolvedCard !== DefaultCardSentinel;

  const data = useProductCardData({
    product,
    productIndex,
    onView,
    viewportThreshold: 0.5,
  });

  const router = useRouter();

  const handleLinkClick = useCallback((): void => {
    onClick(product, productIndex);
  }, [onClick, product, productIndex]);

  /** Analytics + SPA navigation for custom/SDK cards that don't have access to Next.js <Link> */
  const handleSdkLinkClick = useCallback((): void => {
    onClick(product, productIndex);
    void router?.push(data.product.href);
  }, [onClick, product, productIndex, router, data.product.href]);

  const handleAddToCart = useCallback((): void => {
    onQuickAddClick(product, productIndex);
  }, [onQuickAddClick, product, productIndex]);

  if (hasCustomCard) {
    const sdkProps = mapToGridProps(
      {
        product: data.product,
        image: data.image,
        badges: data.badges,
        pricing: data.pricing,
        options: data.options,
        productIndex: data.productIndex,
      },
      { onLinkClick: handleSdkLinkClick, onAddToCart: handleAddToCart }
    );
    return (
      <div ref={data.containerRef} data-testid={testId}>
        <ResolvedCard {...sdkProps} />
      </div>
    );
  }

  return (
    <CardProvider
      product={data.product}
      image={data.image}
      badges={data.badges}
      pricing={data.pricing}
      layout={data.layout}
      hideSponsoredTag={hideSponsoredTag}
      onLinkClick={handleLinkClick}
      onAddToCart={handleAddToCart}
    >
      <CardShell
        ref={data.containerRef}
        data-testid={testId}
        data-placement-index={productIndex}
        $isSmallCard={isSmallCard}
        $isCarousel={isCarousel}
      >
        <CardLink>
          <div>
            <CardImage />
            <CardSponsoredTag />
            <CardName />
            <CardBrand />
          </div>
          <div>
            <CardStrainDetails />
            <CardTags />
          </div>
        </CardLink>
        <div>
          <CardPrice />
          <CardActionButton />
        </div>
      </CardShell>
    </CardProvider>
  );
};

export default memo(Card);

type CardShellProps = React.HTMLAttributes<HTMLDivElement> & {
  $isSmallCard?: boolean;
  $isCarousel?: boolean;
};

/** Reads textAlignment from context to set CSS custom properties on the card wrapper. */
const CardShell = React.forwardRef<HTMLDivElement, CardShellProps>(
  ({ $isSmallCard, $isCarousel, ...props }, ref): JSX.Element => {
    const { textAlignment } = useCardContext();
    return (
      <Wrapper
        ref={ref}
        $isCentered={textAlignment === ProductCardTextAlignmentOption.center}
        $smallCard={$isSmallCard}
        $isCarousel={$isCarousel}
        {...props}
      />
    );
  }
);
CardShell.displayName = 'CardShell';

type CardLinkProps = {
  children: ReactNode;
};

/** Wraps children in a product link. Uses an external link in embedded carousel mode. */
function CardLink({ children }: CardLinkProps): JSX.Element {
  const { href, onLinkClick, isEmbeddedCarousel } = useCardContext();
  const testId = 'card-link';

  if (isEmbeddedCarousel) {
    return (
      <EmbeddedCarouselAnchor href={href} data-testid={testId}>
        {children}
      </EmbeddedCarouselAnchor>
    );
  }

  return (
    <Link href={href} onClick={onLinkClick} data-testid={testId}>
      <AnchorWrapper>{children}</AnchorWrapper>
    </Link>
  );
}

const Wrapper = styled.div<{ $isCentered?: boolean; $smallCard?: boolean; $isCarousel?: boolean }>`
  --text-alignment: ${({ $isCentered }) => ($isCentered ? 'center' : 'left')};
  --justify-content: ${({ $isCentered }) => ($isCentered ? 'center' : 'flex-start')};

  background: #fff;
  border: 1px solid #e3e7e9;
  border-radius: ${({ theme }) => theme.customized.radius.lg};
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 16px;
  position: relative;
  text-align: var(--text-alignment);
  transition: all 0.2s;
  width: ${({ $isCarousel }) => ($isCarousel ? '184px' : '100%')};

  @media screen and (min-width: 1110px) {
    width: ${({ $isCarousel, $smallCard }) => {
      if (!$isCarousel) return '100%';
      return $smallCard ? '184px' : '220px';
    }};
  }

  &:hover {
    border-color: #d2d7da;
    box-shadow: 0 4px 10px 0 #0000000d;
  }
`;

const anchorStyles = css`
  display: block;

  /* Increase click area to entire card */
  &::before {
    content: '';
    height: 100%;
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const AnchorWrapper = styled.div`
  ${anchorStyles}
`;

const EmbeddedCarouselAnchor = styled(EmbeddedExternalLink)`
  ${anchorStyles}
`;
